/**
 * Settings dialogs store — open-state for the two modal settings surfaces.
 *
 * - `workspaceSettings` controls the workspace `<SettingsDialog>` (profile,
 *   security, MCP, datasets, etc.) and remembers which section to land on.
 * - `appSettings` controls the `<AppSettingsDialog>` that wraps the per-app
 *   identity / uninstall view.
 *
 * Both dialogs are mounted once in `AppLayout`. Any descendant component
 * opens them via the actions exported here, so the dialogs don't need to be
 * duplicated next to every trigger.
 */

import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'

interface SettingsDialogsState {
  /**
   * `initialSection` is the left-rail section to land on; `initialSubsection`
   * optionally targets a tab within that section (e.g. the Agent Email page's
   * "senders" tab). Subsection is reset on every open so a normal open never
   * inherits a stale tab.
   */
  workspaceSettings: { open: boolean; initialSection: string; initialSubsection?: string }
  appSettings: { open: boolean }
  openWorkspaceSettings: (section?: string, subsection?: string) => void
  closeWorkspaceSettings: () => void
  openAppSettings: () => void
  closeAppSettings: () => void
}

export const useSettingsDialogsStore = create<SettingsDialogsState>()(
  immer((set) => ({
    workspaceSettings: { open: false, initialSection: 'account', initialSubsection: undefined },
    appSettings: { open: false },

    openWorkspaceSettings: (section, subsection) => {
      set((state) => {
        state.workspaceSettings.open = true
        if (section) state.workspaceSettings.initialSection = section
        state.workspaceSettings.initialSubsection = subsection
      })
    },
    closeWorkspaceSettings: () => {
      set((state) => {
        state.workspaceSettings.open = false
        state.workspaceSettings.initialSubsection = undefined
      })
    },
    openAppSettings: () => {
      set((state) => {
        state.appSettings.open = true
      })
    },
    closeAppSettings: () => {
      set((state) => {
        state.appSettings.open = false
      })
    },
  })),
)
